import { authFetch } from '../../lib/auth-fetch';

interface OfficeThread {
  id: string;
  conversationId?: string;
  title: string;
  lastUser: string;
  lastAssistant: string;
  updatedAt: number;
}

interface SseEvent {
  event: string;
  data: Record<string, unknown>;
}

// One attachment sent alongside a message. Images -> dataUrl (base64), text
// files -> text. Shape mirrors the /api/openclaw/chat backend contract.
export interface OfficeAttachment {
  name?: string;
  mimeType?: string;
  dataUrl?: string;
  text?: string;
}

export interface StreamOfficeEaChatOptions {
  message: string;
  attachments?: OfficeAttachment[];
  onAssistantText: (aggregate: string) => void;
  onDone?: () => void;
  onError?: (msg: string) => void;
  signal?: AbortSignal;
}

const OFFICE_THREADS_UPDATED_EVENT = 'boss-office-threads-updated';

function selectedOperatingLaneId(): 'personal' | 'phoenix' | 'industry-rockstar' {
  const lane = localStorage.getItem('boss_operating_lane');
  return lane === 'personal' || lane === 'industry-rockstar' ? lane : 'phoenix';
}

function threadsKey(): string {
  return `boss_office_threads_v3_${selectedOperatingLaneId()}`;
}

function activeThreadKey(): string {
  return `boss_office_active_thread_v3_${selectedOperatingLaneId()}`;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('boss_token') ?? '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function loadThreads(): OfficeThread[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(threadsKey()) ?? '[]');
    return Array.isArray(parsed) ? parsed.slice(0, 5) : [];
  } catch {
    return [];
  }
}

function titleFrom(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) return 'Atlas conversation';
  return clean.length > 48 ? `${clean.slice(0, 45)}...` : clean;
}

function selectedOperatingLane(): string {
  const lane = localStorage.getItem('boss_operating_lane');
  if (lane === 'personal') return 'Personal';
  if (lane === 'industry-rockstar') return 'Industry Rockstar';
  return 'Phoenix Venture Studios';
}

function buildOfficeInstruction(text: string): string {
  const request = text.trim() || 'Review the attached files or screenshots and use them as context.';
  const selectedLane = selectedOperatingLane();
  return [
    'You are Atlas, Nathan Wildman\'s Chief of Staff and primary conversation partner inside BoS.',
    'Speak as one trusted operator coordinating the whole system, not as Claude, Codex, Hermes, Gio, or a developer console.',
    'Let Nathan think out loud, change direction, disagree, and explore. Reflect what you heard before turning it into work.',
    'Identify the operating lane for every material plan: Personal, Phoenix Venture Studios, or Industry Rockstar. Never blend lane knowledge or commitments silently.',
    `The currently selected dashboard lane is ${selectedLane}. Use it as the starting context, but say when Nathan's request clearly belongs in another lane.`,
    'For a plan, discuss what is known, what is assumed, what is missing, the strongest tradeoff, the recommended path, and the next checkpoint.',
    'Give every proposed action exactly one accountable owner: Nathan, Atlas/BoS agent, a named specialist, Codex, n8n, or a named outside person.',
    'Use Nathan\'s status labels: Ready, Working, Needs you, Blocked, or Review.',
    'Ask no more than one high-value clarifying question at a time. Make a useful recommendation even when some information is missing.',
    'For planning or brainstorming, stay in discussion mode. Do not create tasks, activate agents, enable schedules, or call external-write tools.',
    'When the plan is mature, present a review-only proposal with owners and say plainly that nothing is assigned yet. Nathan can keep discussing it or say exactly "Lock that plan".',
    '"Lock that plan" authorizes only the displayed internal BoS work. Email, CRM, social, calendar, purchases, production deployments, DNS, and schedules always need their own exact approval.',
    'For an explicit execution request, use only the currently approved scope, route the smallest bounded work packet, and report the owner, status, evidence, and next checkpoint.',
    'Keep specialists behind the scenes unless Nathan asks to hear from one. Return one joined-up answer rather than making Nathan manage a crowd of agents.',
    'Codex CLI is the default working engine. Never silently switch to Claude, Gemini, an API, or another provider. Claude and Gemini are optional outside judges only when Nathan approves a bounded reason and source packet.',
    'Learn only from Nathan\'s locked decisions or explicit teaching. Brainstorming, rejected ideas, and outside-model opinions are not durable truth.',
    'Be plain-English, direct, practical, and conversational. Lead with what is true, what is missing, and what happens next.',
    'Start the final response with a section exactly titled "Voice summary:" followed by 1-3 concise, human spoken sentences.',
    'Do not read out internal reasoning, tool use, commands, logs, or implementation play-by-play. The Voice summary should say what was discussed or done, who owns what, and what Nathan needs to decide next.',
    '',
    `CEO request: ${request}`,
  ].join('\n');
}

function dispatchOfficeThreadsUpdated(): void {
  window.dispatchEvent(new Event(OFFICE_THREADS_UPDATED_EVENT));
}

export function resetOfficeConversation(): void {
  const threads = loadThreads();
  const activeId = localStorage.getItem(activeThreadKey()) || '';
  const remaining = threads.filter((thread) => thread.id !== activeId);
  localStorage.setItem(threadsKey(), JSON.stringify(remaining));
  if (remaining[0]) localStorage.setItem(activeThreadKey(), remaining[0].id);
  else localStorage.removeItem(activeThreadKey());
  dispatchOfficeThreadsUpdated();
}

function updateOfficeThread(patch: Partial<OfficeThread>): OfficeThread {
  const threads = loadThreads();
  const activeThreadId = localStorage.getItem(activeThreadKey()) || '';
  const activeThread = threads.find((thread) => thread.id === activeThreadId) ?? threads[0];
  const base: OfficeThread = activeThread ?? {
    id: `office-${Date.now()}`,
    title: 'Atlas conversation',
    lastUser: '',
    lastAssistant: '',
    updatedAt: Date.now(),
  };
  const nextThread = { ...base, ...patch, updatedAt: Date.now() };
  const clipped = [
    nextThread,
    ...threads.filter((thread) => thread.id !== nextThread.id),
  ]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 5);

  localStorage.setItem(threadsKey(), JSON.stringify(clipped));
  localStorage.setItem(activeThreadKey(), nextThread.id);
  dispatchOfficeThreadsUpdated();
  return nextThread;
}

function parseSseEvents(chunk: string): SseEvent[] {
  return chunk
    .split('\n\n')
    .map((block) => {
      let event = 'message';
      let data = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (!data) return null;
      try {
        return { event, data: JSON.parse(data) as Record<string, unknown> };
      } catch {
        return null;
      }
    })
    .filter((entry): entry is SseEvent => entry !== null);
}

export async function streamOfficeEaChat(opts: StreamOfficeEaChatOptions): Promise<void> {
  const visibleUserText = opts.message.trim();
  let thread = updateOfficeThread({
    title: titleFrom(visibleUserText),
    lastUser: visibleUserText,
  });

  const interruptActiveTurn = (): void => {
    if (!thread.conversationId) return;
    void authFetch('api/openclaw/chat/interrupt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ conversationId: thread.conversationId }),
    }).catch(() => undefined);
  };
  opts.signal?.addEventListener('abort', interruptActiveTurn, { once: true });

  try {
    const res = await authFetch('api/openclaw/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        message: buildOfficeInstruction(visibleUserText),
        assistant: 'atlas',
        lane: selectedOperatingLaneId(),
        conversationId: thread.conversationId,
        newConversation: !thread.conversationId,
        attachments: opts.attachments ?? [],
      }),
      signal: opts.signal,
    });
    if (!res.ok || !res.body) throw new Error(`Office request failed (${res.status})`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let aggregate = '';
    let streamError = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const boundary = buffer.lastIndexOf('\n\n');
      if (boundary === -1) continue;
      const complete = buffer.slice(0, boundary + 2);
      buffer = buffer.slice(boundary + 2);
      for (const item of parseSseEvents(complete)) {
        if (item.event === 'conversation' && typeof item.data.conversationId === 'string') {
          thread = updateOfficeThread({ conversationId: item.data.conversationId });
        }
        if (item.event === 'message' && typeof item.data.text === 'string') {
          aggregate += item.data.text;
          opts.onAssistantText(aggregate);
          updateOfficeThread({ conversationId: thread.conversationId, lastAssistant: aggregate });
        }
        if (item.event === 'error') {
          const detail = String(item.data.message ?? item.data.stderrTail ?? 'Office turn failed');
          streamError = detail;
          updateOfficeThread({ lastAssistant: detail });
          opts.onError?.(detail);
        }
      }
    }

    if (streamError && !aggregate.trim()) throw new Error(streamError);
    if (!aggregate.trim()) throw new Error('Atlas returned no response.');
    updateOfficeThread({ conversationId: thread.conversationId, lastAssistant: aggregate.trim() });
    opts.onDone?.();
  } finally {
    opts.signal?.removeEventListener('abort', interruptActiveTurn);
  }
}
